import type { Rewards } from "../../common/jsonRpcTypes";

/**
 * Booking sources that can earn DesignMyNight reward points.
 *
 * Mirrors `Booking::SOURCE_DMN`, `SOURCE_DMN_ADMIN` and `SOURCE_DMN_MOBILE`,
 * which are the only sources accepted by
 * `BookingRewardTransaction::isRewardValidEver()`. This must stay an allow-list:
 * checking `source !== "partner"` wrongly lets sources such as `tonic` or
 * `admin` advertise points that will never be awarded.
 */
export const REWARD_ELIGIBLE_SOURCES: readonly string[] = [
  "designmynight",
  "designmynight-admin",
  "designmynight-mobile",
];

/**
 * Booking type excluded from rewards by
 * `BookingRewardTransaction::isValidBookingType()`.
 */
const CONCIERGE_BOOKING_TYPE = "concierge";

export interface RewardsEligibilityInput {
  /** Reward figures returned by `/createBooking`. */
  readonly rewards?: Rewards | null;
  /** Booking source, e.g. "designmynight". */
  readonly source?: string;
  /** Affiliate id, when the booking came through an affiliate. */
  readonly affiliateId?: string | null;
  /** Booking type name, used to exclude concierge bookings. */
  readonly bookingTypeName?: string | null;
  /**
   * Availability outcome. Only supplied on the booking details step, where a
   * rejected or re-suggested time must not advertise points.
   */
  readonly availabilityAction?: string | null;
  /** Whether the guest is submitting an enquiry rather than a booking. */
  readonly isEnquiry?: boolean;
}

/**
 * Decide whether reward points may be advertised for a booking.
 *
 * This reproduces Collins' own award rules so the widget never promises points
 * that `BookingRewardTransaction` will refuse to grant. Collins returns the
 * `rewards` figures unconditionally — they are populated for every successful
 * booking check regardless of source, affiliate or whether reward messaging is
 * switched on — so the widget cannot simply trust their presence.
 *
 * Known limitation: Collins exposes no `showRewardMessaging()` flag over the
 * API, so a non-zero points value is the closest available proxy. If reward
 * messaging were disabled globally while `points_per_unit` stayed configured,
 * the banner would still show. Removing that gap needs a flag on the
 * `/createBooking` response.
 *
 * Not yet enforced: DesignMyNight rewards are advertised as UK-only, but
 * Collins applies no country check and `pointsToCurrency()` hard-codes GBP.
 * Pending a decision on non-GB venues, no country gate is applied here.
 */
export function isRewardsEligible(input: RewardsEligibilityInput): boolean {
  const { rewards, source, affiliateId, bookingTypeName, availabilityAction, isEnquiry } = input;

  if (!rewards || !Number.isFinite(rewards.points) || rewards.points <= 0) return false;
  if (!source || !REWARD_ELIGIBLE_SOURCES.includes(source)) return false;
  if (affiliateId) return false;
  if (bookingTypeName?.toLowerCase() === CONCIERGE_BOOKING_TYPE) return false;

  // An enquiry is still submitted for a time the venue has yet to confirm, so
  // a re-suggested time does not block it — matching the legacy
  // `availability.action != 'suggest_times' || booking.do_enquiry` guard.
  if (availabilityAction === "reject") return false;
  if (availabilityAction === "suggest_times" && !isEnquiry) return false;

  return true;
}

/**
 * Convert a points balance into its sterling value.
 *
 * Collins only sends the conversion rate implicitly, as the pair
 * (`redeemMinimumAmount`, `redeemMinimumValue`), so the rate is recovered from
 * those. Returns undefined when the rate cannot be derived, so callers can fall
 * back to showing the points total on its own.
 */
export function pointsToCurrencyValue(points: number, rewards: Rewards): number | undefined {
  const { redeemMinimumAmount, redeemMinimumValue } = rewards;
  if (!redeemMinimumAmount || redeemMinimumValue == null || redeemMinimumValue <= 0) return undefined;
  const pointsPerUnit = redeemMinimumAmount / redeemMinimumValue;
  if (!Number.isFinite(pointsPerUnit) || pointsPerUnit <= 0) return undefined;
  return Math.round((points / pointsPerUnit) * 100) / 100;
}
